import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import UniversalModal from '../Common/UniversalModal';
import Toast from '../Common/Toast';
import '../Common/CardActions.css';
import Pagination from '../Common/Pagination';

const emptyForm = {
  fedresurs_id: '',
  name: '',
  short_name: '',
  inn: '',
  ogrn: '',
  address: '',
};
const SRO_COL_WIDTH_DEFAULTS = {
  name: 320,
  short_name: 260,
  inn: 140,
  ogrn: 160,
  address: 360,
  actions: 120,
};
const SRO_COL_WIDTH_LIMITS = { min: 80, max: 900 };
const SROS_TABLE_WIDTHS_CACHE_KEY = 'aot_sros_table_col_widths_v1';

const normalizeSroColWidths = (raw) => {
  const next = { ...SRO_COL_WIDTH_DEFAULTS };
  if (!raw || typeof raw !== 'object') return next;
  Object.keys(SRO_COL_WIDTH_DEFAULTS).forEach((key) => {
    const n = Number(raw[key]);
    if (!Number.isFinite(n)) return;
    next[key] = Math.max(SRO_COL_WIDTH_LIMITS.min, Math.min(SRO_COL_WIDTH_LIMITS.max, Math.round(n)));
  });
  return next;
};

const readCachedSroColWidths = () => {
  try {
    const raw = localStorage.getItem(SROS_TABLE_WIDTHS_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
};

const isAdminUser = (user) => {
  return !!user?.is_staff || !!user?.is_superuser || String(user?.role || '').toLowerCase() === 'admin';
};

const formatImportStatus = (s) => {
  if (s === 'running') return 'Выполняется';
  if (s === 'pending') return 'В очереди';
  if (s === 'done') return 'Завершено';
  if (s === 'failed') return 'Ошибка';
  return s || '—';
};

const SrosList = () => {
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(24);
  const [sroColWidths, setSroColWidths] = useState(readCachedSroColWidths);

  const [modalState, setModalState] = useState({ isOpen: false, mode: null, item: null }); // create|view|edit
  const [formData, setFormData] = useState(emptyForm);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [importJob, setImportJob] = useState(null);
  const importJobRef = useRef(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importStarting, setImportStarting] = useState(false);
  const [shortNameJob, setShortNameJob] = useState(null);
  const shortNameJobRef = useRef(null);
  const [shortNameLoading, setShortNameLoading] = useState(false);
  const [shortNameStarting, setShortNameStarting] = useState(false);
  const importFileRef = useRef(null);
  const sroColWidthsRef = useRef(sroColWidths);
  const resizeStateRef = useRef(null);
  const resizeRafRef = useRef(null);

  useEffect(() => {
    sroColWidthsRef.current = sroColWidths;
  }, [sroColWidths]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2500);
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const all = [];
      let page = 1;
      while (true) {
        const resp = await apiService.getSros(`?page=${page}`);
        const chunk = resp.data.results || [];
        all.push(...chunk);
        if (!resp.data.next) break;
        page += 1;
        if (page > 200) break;
      }
      setItems(all);
    } catch (err) {
      setError('Ошибка при загрузке СРО: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiService.getUiSettings();
        const v = Number(resp?.data?.table_page_size);
        if ([12, 24, 48, 96].includes(v)) {
          setItemsPerPage(v);
        }
        if (isAdmin) {
          const widths = resp?.data?.sros_table_column_widths;
          if (widths && typeof widths === 'object') {
            setSroColWidths(widths);
            localStorage.setItem(SROS_TABLE_WIDTHS_CACHE_KEY, JSON.stringify(widths));
          }
        }
      } catch (_) {
        // ignore
      }
    })();
  }, [isAdmin]);

  const loadLatestImportJob = useCallback(async () => {
    if (!isAdmin) return;
    setImportLoading(true);
    try {
      const resp = await apiService.listSroImportJobs('?page=1&job_type=import');
      const first = (resp.data?.results || [])[0] || null;
      setImportJob(first);
    } catch (err) {
      console.error('Не удалось загрузить статус импорта СРО', err);
    } finally {
      setImportLoading(false);
    }
  }, [isAdmin]);

  const loadLatestShortNameJob = useCallback(async () => {
    if (!isAdmin) return;
    setShortNameLoading(true);
    try {
      const resp = await apiService.listSroImportJobs('?page=1&job_type=short_name_update');
      const first = (resp.data?.results || [])[0] || null;
      setShortNameJob(first);
    } catch (err) {
      console.error('Не удалось загрузить статус обновления сокращенных наименований СРО', err);
    } finally {
      setShortNameLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isAdmin) loadLatestImportJob(); }, [isAdmin, loadLatestImportJob]);
  useEffect(() => { if (isAdmin) loadLatestShortNameJob(); }, [isAdmin, loadLatestShortNameJob]);

  useEffect(() => { importJobRef.current = importJob; }, [importJob]);
  useEffect(() => { shortNameJobRef.current = shortNameJob; }, [shortNameJob]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    const timer = setInterval(async () => {
      const job = importJobRef.current;
      if (!job?.id) return;
      if (job.status !== 'pending' && job.status !== 'running') return;
      try {
        const resp = await apiService.getSroImportJob(job.id);
        setImportJob(resp.data);
      } catch (err) {
        // ignore
      }
    }, 1200);
    return () => clearInterval(timer);
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    const timer = setInterval(async () => {
      const job = shortNameJobRef.current;
      if (!job?.id) return;
      if (job.status !== 'pending' && job.status !== 'running') return;
      try {
        const resp = await apiService.getSroImportJob(job.id);
        setShortNameJob(resp.data);
      } catch (err) {
        // ignore
      }
    }, 1200);
    return () => clearInterval(timer);
  }, [isAdmin]);

  const startImport = async () => {
    if (!isAdmin) return;
    setImportStarting(true);
    try {
      const resp = await apiService.startSroImport();
      setImportJob(resp.data);
      showToast('Импорт СРО запущен');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      showToast('Не удалось запустить импорт СРО: ' + msg, 'error');
    } finally {
      setImportStarting(false);
    }
  };

  const startShortNameUpdate = async () => {
    if (!isAdmin) return;
    setShortNameStarting(true);
    try {
      const resp = await apiService.startSroShortNameUpdate();
      setShortNameJob(resp.data);
      showToast('Обновление сокращенных наименований запущено');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      showToast('Не удалось запустить обновление: ' + msg, 'error');
    } finally {
      setShortNameStarting(false);
    }
  };

  const refreshShortNameStatus = async () => {
    if (!isAdmin) return;
    if (!shortNameJob?.id) return;
    try {
      const resp = await apiService.getSroImportJob(shortNameJob.id);
      setShortNameJob(resp.data);
    } catch (err) {
      // ignore
    }
  };

  const refreshImportStatus = async () => {
    if (!isAdmin) return;
    if (!importJob?.id) return;
    try {
      const resp = await apiService.getSroImportJob(importJob.id);
      setImportJob(resp.data);
    } catch (err) {
      // ignore
    }
  };

  const handleExport = async () => {
    if (!isAdmin) return;
    try {
      const resp = await apiService.exportSros();
      const blob = new Blob([resp.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sros_export.json';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showToast('Экспорт выполнен');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      showToast('Ошибка экспорта: ' + msg, 'error');
    }
  };

  const handleImportClick = () => {
    if (!isAdmin) return;
    importFileRef.current?.click();
  };

  const handleImportFile = async (e) => {
    if (!isAdmin) return;
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const formDataFile = new FormData();
      formDataFile.append('file', file);
      const resp = await apiService.importSros(formDataFile);
      const { created = 0, updated = 0, skipped = 0 } = resp.data || {};
      showToast(`Импорт выполнен: создано ${created}, обновлено ${updated}, пропущено ${skipped}`);
      load();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || err.message;
      showToast('Ошибка импорта: ' + msg, 'error');
    } finally {
      e.target.value = '';
    }
  };

  const filtered = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return items;
    return (items || []).filter((s) => {
      const hay = `${s.name || ''} ${s.short_name || ''} ${s.inn || ''} ${s.ogrn || ''} ${s.address || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, itemsPerPage, items]);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filtered.slice(start, start + itemsPerPage);
  }, [filtered, currentPage, itemsPerPage]);

  const handleItemsPerPageChange = (v) => {
    setItemsPerPage(v);
    setCurrentPage(1);
    apiService.saveUiSettings({ table_page_size: v }).catch(() => {});
  };
  const effectiveSroColWidths = useMemo(() => normalizeSroColWidths(sroColWidths), [sroColWidths]);

  const persistSroColWidthsCache = useCallback((next) => {
    try {
      localStorage.setItem(SROS_TABLE_WIDTHS_CACHE_KEY, JSON.stringify(next || {}));
    } catch (_) {}
  }, []);

  const startResizeColumn = useCallback((e, key) => {
    if (!isAdmin) return;
    e.preventDefault();
    e.stopPropagation();
    resizeStateRef.current = {
      key,
      startX: e.clientX,
      startWidth: Number(effectiveSroColWidths[key] || 100),
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [effectiveSroColWidths, isAdmin]);

  useEffect(() => {
    const onMove = (e) => {
      const state = resizeStateRef.current;
      if (!state) return;
      const delta = e.clientX - state.startX;
      const nextWidth = Math.max(SRO_COL_WIDTH_LIMITS.min, Math.min(SRO_COL_WIDTH_LIMITS.max, state.startWidth + delta));
      if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current);
      resizeRafRef.current = requestAnimationFrame(() => {
        setSroColWidths((prev) => {
          const next = { ...(prev || {}), [state.key]: Math.round(nextWidth) };
          persistSroColWidthsCache(next);
          return next;
        });
      });
    };
    const onUp = () => {
      if (!resizeStateRef.current) return;
      resizeStateRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      const normalized = normalizeSroColWidths(sroColWidthsRef.current || {});
      setSroColWidths(normalized);
      persistSroColWidthsCache(normalized);
      if (isAdmin) {
        apiService.saveUiSettings({ sros_table_column_widths: normalized }).catch(() => {
          setError('Не удалось сохранить ширины колонок СРО');
        });
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isAdmin, persistSroColWidthsCache]);

  const openCreate = () => {
    setFormData(emptyForm);
    setHasUnsavedChanges(false);
    setModalState({ isOpen: true, mode: 'create', item: null });
  };

  const openView = (item) => {
    setFormData({
      fedresurs_id: item?.fedresurs_id || '',
      name: item?.name || '',
      short_name: item?.short_name || '',
      inn: item?.inn || '',
      ogrn: item?.ogrn || '',
      address: item?.address || '',
    });
    setHasUnsavedChanges(false);
    setModalState({ isOpen: true, mode: 'view', item });
  };

  const openEdit = (item) => {
    setFormData({
      fedresurs_id: item?.fedresurs_id || '',
      name: item?.name || '',
      short_name: item?.short_name || '',
      inn: item?.inn || '',
      ogrn: item?.ogrn || '',
      address: item?.address || '',
    });
    setHasUnsavedChanges(false);
    setModalState({ isOpen: true, mode: 'edit', item });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, mode: null, item: null });
    setFormData(emptyForm);
    setHasUnsavedChanges(false);
  };

  const onField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
  };

  const handleSave = async ({ exit } = {}) => {
    if (!isAdmin) return;
    try {
      let saved = null;
      if (modalState.item?.id) {
        const resp = await apiService.updateSro(modalState.item.id, formData);
        saved = resp.data;
        setItems((prev) => (prev || []).map((x) => (x.id === saved.id ? saved : x)));
        showToast('Изменения сохранены');
      } else {
        const resp = await apiService.createSro(formData);
        saved = resp.data;
        setItems((prev) => [saved, ...(prev || [])]);
        showToast('СРО создано');
      }
      setHasUnsavedChanges(false);
      if (exit) {
        closeModal();
      } else {
        openEdit(saved);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.error || err.message;
      setError('Ошибка при сохранении СРО: ' + msg);
      showToast('Ошибка при сохранении СРО', 'error');
    }
  };

  const handleDelete = async (item) => {
    if (!isAdmin) return;
    if (!window.confirm(`Удалить СРО ${item?.name || ''}?`)) return;
    try {
      await apiService.deleteSro(item.id);
      setItems((prev) => (prev || []).filter((x) => x.id !== item.id));
      showToast('СРО удалено');
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.error || err.message;
      setError('Ошибка при удалении СРО: ' + msg);
      showToast('Ошибка при удалении СРО', 'error');
    }
  };

  if (loading) {
    return <div style={{ padding: 50, textAlign: 'center' }}><h2>Загрузка СРО...</h2></div>;
  }

  const progressPct = Math.max(
    0,
    Math.min(100, (importJob?.total_pages ? (importJob.current_page / importJob.total_pages) * 100 : 0)),
  );

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1>🏛️ СРО</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {isAdmin && (
            <input
              ref={importFileRef}
              type="file"
              accept="application/json"
              style={{ display: 'none' }}
              onChange={handleImportFile}
            />
          )}
          <button
            type="button"
            onClick={load}
            style={{ padding: '10px 16px', background: '#f8f9fa', color: '#2c3e50', border: '1px solid #dee2e6', borderRadius: 8, cursor: 'pointer' }}
          >
            Обновить
          </button>
          {isAdmin && (
            <>
              <button
                type="button"
                onClick={handleExport}
                style={{ padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}
              >
                Экспорт
              </button>
              <button
                type="button"
                onClick={handleImportClick}
                style={{ padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}
              >
                Импорт
              </button>
              <button
                type="button"
                onClick={startImport}
                disabled={importStarting || importJob?.status === 'running' || importJob?.status === 'pending'}
                style={{ padding: '10px 16px', background: '#0d6efd', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}
                title="Импорт с Федресурса (old.bankrot.fedresurs.ru)"
              >
                {importStarting ? 'Запуск…' : 'Импорт с Федресурса'}
              </button>
              <button
                type="button"
                onClick={startShortNameUpdate}
                disabled={shortNameStarting || shortNameJob?.status === 'running' || shortNameJob?.status === 'pending'}
                style={{ padding: '10px 16px', background: '#28a745', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}
                title="Обновить сокращенные наименования по egrul.itsoft.ru"
              >
                {shortNameStarting ? 'Запуск…' : 'Обновить сокращенные'}
              </button>
              <button
                type="button"
                onClick={openCreate}
                style={{ padding: '10px 16px', background: '#007bff', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}
              >
                Добавить
              </button>
            </>
          )}
        </div>
      </div>

      {isAdmin && (
        <div style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 12, padding: 14, marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ color: '#6c757d' }}>
              Статус импорта: <b>{formatImportStatus(importJob?.status)}</b>
              {importLoading && <span> · загрузка…</span>}
            </div>
            <button type="button" onClick={refreshImportStatus} disabled={!importJob?.id} style={{ padding: '8px 12px', border: '1px solid #dee2e6', borderRadius: 8, background: '#f8f9fa' }}>
              Обновить статус
            </button>
            <div style={{ color: '#6c757d' }}>
              {importJob
                ? `стр. ${importJob.current_page || 0}/${importJob.total_pages || 0} · обработано ${importJob.processed || 0} · создано ${importJob.created || 0} · обновлено ${importJob.updated || 0} · пропущено ${importJob.skipped || 0}`
                : '—'}
            </div>
          </div>
          <div style={{ marginTop: 10, height: 10, background: '#e9ecef', borderRadius: 999, overflow: 'hidden' }}>
            <div style={{ width: `${progressPct}%`, height: '100%', background: importJob?.status === 'failed' ? '#dc3545' : '#0d6efd', transition: 'width 0.2s ease' }} />
          </div>
          {!!importJob?.message && (
            <div style={{ marginTop: 10, color: '#495057', whiteSpace: 'pre-wrap' }}>
              {String(importJob.message).trim()}
            </div>
          )}
        </div>
      )}

      {isAdmin && (
        <div style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 12, padding: 14, marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ color: '#6c757d' }}>
              Обновление сокращенных: <b>{formatImportStatus(shortNameJob?.status)}</b>
              {shortNameLoading && <span> · загрузка…</span>}
            </div>
            <button type="button" onClick={refreshShortNameStatus} disabled={!shortNameJob?.id} style={{ padding: '8px 12px', border: '1px solid #dee2e6', borderRadius: 8, background: '#f8f9fa' }}>
              Обновить статус
            </button>
            <div style={{ color: '#6c757d' }}>
              {shortNameJob
                ? `обработано ${shortNameJob.processed || 0} · обновлено ${shortNameJob.updated || 0} · пропущено ${shortNameJob.skipped || 0}`
                : '—'}
            </div>
          </div>
          {!!shortNameJob?.message && (
            <div style={{ marginTop: 10, color: '#495057', whiteSpace: 'pre-wrap' }}>
              {String(shortNameJob.message).trim()}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по СРО, ИНН, ОГРН, адресу..."
          style={{ flex: 1, minWidth: 260, padding: 10, border: '1px solid #dee2e6', borderRadius: 10 }}
        />
        <div style={{ color: '#6c757d', whiteSpace: 'nowrap' }}>
          Найдено: <b>{filtered.length}</b>
        </div>
      </div>

      {error && (
        <div style={{ background: '#fee', border: '1px solid #fcc', color: '#a00', padding: 12, borderRadius: 8, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 12, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <colgroup>
            <col style={{ width: effectiveSroColWidths.name }} />
            <col style={{ width: effectiveSroColWidths.short_name }} />
            <col style={{ width: effectiveSroColWidths.inn }} />
            <col style={{ width: effectiveSroColWidths.ogrn }} />
            <col style={{ width: effectiveSroColWidths.address }} />
            {isAdmin && <col style={{ width: effectiveSroColWidths.actions }} />}
          </colgroup>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              {[
                { key: 'name', label: 'Наименование' },
                { key: 'short_name', label: 'Сокр. наименование' },
                { key: 'inn', label: 'ИНН' },
                { key: 'ogrn', label: 'ОГРН' },
                { key: 'address', label: 'Адрес (Росреестр)' },
              ].map((col) => (
                <th key={col.key} style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e9ecef', position: 'relative' }}>
                  <span>{col.label}</span>
                  {!!isAdmin && (
                    <span
                      role="presentation"
                      onMouseDown={(e) => startResizeColumn(e, col.key)}
                      onClick={(e) => e.stopPropagation()}
                      title="Изменить ширину столбца"
                      style={{ position: 'absolute', top: 0, right: -2, width: 10, height: '100%', cursor: 'col-resize' }}
                    />
                  )}
                </th>
              ))}
              {isAdmin && (
                <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e9ecef', position: 'relative' }}>
                  <span>Действия</span>
                  <span
                    role="presentation"
                    onMouseDown={(e) => startResizeColumn(e, 'actions')}
                    onClick={(e) => e.stopPropagation()}
                    title="Изменить ширину столбца"
                    style={{ position: 'absolute', top: 0, right: -2, width: 10, height: '100%', cursor: 'col-resize' }}
                  />
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? 6 : 5} style={{ padding: 20, textAlign: 'center', color: '#6c757d', fontSize: '13px' }}>
                  СРО не найдены
                </td>
              </tr>
            ) : paginated.map((s) => (
              <tr key={s.id} onClick={() => openView(s)} style={{ cursor: 'pointer' }}>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid #eef1f4', fontSize: '13px' }}>{s.name || '—'}</td>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid #eef1f4', fontSize: '13px' }}>{s.short_name || '—'}</td>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid #eef1f4', fontSize: '13px' }}>{s.inn || '—'}</td>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid #eef1f4', fontSize: '13px' }}>{s.ogrn || '—'}</td>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid #eef1f4', fontSize: '13px' }}>{s.address || '—'}</td>
                {isAdmin && (
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #eef1f4', fontSize: '13px' }}>
                    <div className="card-actions" style={{ justifyContent: 'flex-start' }} onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="card-action-btn card-action-btn--edit"
                        title="Редактировать"
                        onClick={() => openEdit(s)}
                      >
                        ✎
                      </button>
                      <button
                        type="button"
                        className="card-action-btn card-action-btn--delete"
                        title="Удалить"
                        onClick={() => handleDelete(s)}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      <UniversalModal
        isOpen={modalState.isOpen}
        title={
          modalState.mode === 'create'
            ? 'Создание СРО'
            : modalState.mode === 'edit'
              ? 'Редактирование СРО'
              : 'Просмотр СРО'
        }
        onClose={closeModal}
        showUnsavedWarning={hasUnsavedChanges}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontWeight: 700, marginBottom: 6 }}>Наименование СРО</label>
            <input
              type="text"
              value={formData.name}
              disabled={modalState.mode === 'view'}
              onChange={(e) => onField('name', e.target.value)}
              style={{ width: '100%', padding: 10, border: '1px solid #dee2e6', borderRadius: 10 }}
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontWeight: 700, marginBottom: 6 }}>Сокращенное наименование</label>
            <input
              type="text"
              value={formData.short_name}
              disabled={modalState.mode === 'view'}
              onChange={(e) => onField('short_name', e.target.value)}
              style={{ width: '100%', padding: 10, border: '1px solid #dee2e6', borderRadius: 10 }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 700, marginBottom: 6 }}>ИНН</label>
            <input
              type="text"
              value={formData.inn}
              disabled={modalState.mode === 'view'}
              onChange={(e) => onField('inn', e.target.value)}
              style={{ width: '100%', padding: 10, border: '1px solid #dee2e6', borderRadius: 10 }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 700, marginBottom: 6 }}>ОГРН</label>
            <input
              type="text"
              value={formData.ogrn}
              disabled={modalState.mode === 'view'}
              onChange={(e) => onField('ogrn', e.target.value)}
              style={{ width: '100%', padding: 10, border: '1px solid #dee2e6', borderRadius: 10 }}
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontWeight: 700, marginBottom: 6 }}>Адрес (по данным Росреестра)</label>
            <textarea
              value={formData.address}
              disabled={modalState.mode === 'view'}
              onChange={(e) => onField('address', e.target.value)}
              rows={4}
              style={{ width: '100%', padding: 10, border: '1px solid #dee2e6', borderRadius: 10, resize: 'vertical' }}
            />
          </div>
          {isAdmin && modalState.mode !== 'view' && (
            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 6 }}>
              <button
                type="button"
                onClick={() => handleSave({ exit: false })}
                style={{ padding: '10px 16px', background: '#007bff', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer' }}
              >
                Сохранить
              </button>
              <button
                type="button"
                onClick={() => handleSave({ exit: true })}
                style={{ padding: '10px 16px', background: '#2d8cff', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer' }}
              >
                Сохранить и выйти
              </button>
            </div>
          )}
        </div>
      </UniversalModal>

      {toast && <Toast message={toast.message} type={toast.type} />}
    </div>
  );
};

export default SrosList;
